import React from 'react'
import { observer, memo } from '~/ui/component'
import { createUseStyles, layout } from '~/ui/styling'
import { register } from './registry'
import { FeedHomeScreenWidget, Media, Post } from '~/models'
import HomeScreenWidget from './HomeScreenWidget'
import { useNewsService } from '../../news'
import { Chip, ClearButton, Grid, HBox, Label, LinkLabel, Panel, PushButton, Tappable, VBox } from '~/ui/components'
import { Avatar, ImageView } from '../../media'
import { useTranslation } from 'react-i18next'
import { useSize } from '~/lib/react-measure'
import { useMediaQuery } from 'react-responsive'
import { screenMinWidths } from '~/ui/styling/layout'

export interface Props {
  options: FeedHomeScreenWidget['options']
}

const FeedWidget = observer('FeedWidget', (props: Props) => {

  const {feed} = props.options

  const service = useNewsService()

  const [t] = useTranslation('home')

  const endpoint = React.useMemo(() => {
    return service.createEndpoint()
  }, [service])

  const isMobile = !useMediaQuery({minWidth: screenMinWidths.tablet})

  const gridRef = React.useRef<HTMLDivElement>(null)
  const [gridWidth, setGridWidth] = React.useState<number | null>(0)
  useSize(gridRef, size => setGridWidth(size.width))

  const columns = React.useMemo(
    () => gridWidth === null ? 1 : Math.floor(gridWidth / (postWidth + itemGap)),
    [gridWidth],
  )

  const postCount = React.useMemo(() => {
    if (columns === 1) { return 4 }
    if (columns <= 3) { return columns * 2 }
    return columns
  }, [columns])

  const ready = service.started

  React.useEffect(() => {
    if (!ready) { return }
    endpoint.resetAndFetch({
      feedIDs: feed == null ? undefined : [feed.id],
      limit:   postCount,
    })
  }, [endpoint, feed, postCount, ready])

  const posts = endpoint.data

  //------
  // Rendering

  function render() {
    return (
      <HomeScreenWidget title={feed?.title ?? t('feed')} right={isMobile ? null : renderMoreButton()}>
        <VBox gap={layout.padding.l}>
          <Grid columns={`repeat(${columns}, 1fr)`} gap={itemGap} ref={gridRef}>
            {posts.map(renderPost)}
          </Grid>
          {isMobile && (
            <VBox align='center'>
              {renderMoreButton()}
            </VBox>
          )}
        </VBox>
      </HomeScreenWidget>
    )
  }

  function renderMoreButton() {
    const href = feed == null ? '//news' : `//news?feedID=${feed.id}`
    return (
      <ClearButton
        href={href}
        caption={t('news:more')}
        icon='arrow-right'
        iconSide='right'
      />
    )
  }

  function renderPost(post: Post) {
    return (
      <FeedWidgetPost
        key={post.id}
        post={post}
      />
    )
  }

  return render()

})

export default FeedWidget

interface FeedWidgetPostProps {
  post: Post
}

const FeedWidgetPost = memo('FeedWidgetPost', (props: FeedWidgetPostProps) => {

  const {post} = props

  const [t] = useTranslation('news')

  const featuredMedia = React.useMemo(
    (): Media | undefined => post.media.filter(it => /^(image)\//.test(it.contentType))[0],
    [post.media],
  )

  const href = `//post/${post.id}`

  //------
  // Rendering

  const $ = useStyles()

  function render() {
    return (
      <Panel classNames={$.post} justify='space-between'>
        <VBox>
          {renderHeader()}
          {renderContent()}
        </VBox>
        {renderFooter()}
      </Panel>
    )
  }

  function renderHeader() {
    return (
      <VBox>
        <HBox gap={layout.padding.inline.s} padding={layout.padding.s}>
          <Avatar
            firstName={post.author.firstName}
            source={post.author.photoURL}
            size={avatarSize}
          />
          <VBox gap={layout.padding.inline.xs}>
            <Label small bold>
              {post.author.name}
            </Label>
            <HBox gap={layout.padding.inline.s}>
              <Chip small backgroundColor={post.feed.color}>
                {post.feed.title}
              </Chip>
              {post.publishedAt != null && (
                <LinkLabel tiny dim href={href}>
                  {post.publishedAt.toFormat('MMM d, HH:mm')}
                </LinkLabel>
              )}
            </HBox>
          </VBox>
        </HBox>
      </VBox>
    )
  }

  function renderContent() {
    return (
      <VBox gap={layout.padding.s}>
        {featuredMedia != null && (
          <Tappable href={href}>
            <ImageView
              source={featuredMedia.url}
              classNames={$.postImage}
            />
          </Tappable>
        )}
        <VBox paddingHorizontal={layout.padding.s}>
          <Label truncate={false}>
            {post.summary}
          </Label>
        </VBox>
      </VBox>
    )
  }

  function renderFooter() {
    return (
      <VBox padding={layout.padding.s}>
        <PushButton
          caption={t('post.view')}
          href={href}
        />
      </VBox>
    )
  }

  return render()
})

export const avatarSize = {
  width:  32,
  height: 32,
}

export const postWidth = 270

export const itemGap = layout.padding.inline.l

const useStyles = createUseStyles({
  post: {
    overflow: 'hidden',
  },

  postImage: {
    aspectRatio: '16 / 9',
  },

  postContent: {
    backgroundSize: 'cover',
  },
})

register('feed', FeedWidget)